import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Plus, Eye } from 'lucide-react';
import './GameModule.css';

import { 
  fetchModulesByCreator, 
  fetchModulesByStatus, 
  createGameModule, 
  updateGameModule, 
  deleteGameModule, 
  fetchGameModules 
} from './GameModuleAPI';

const client = generateClient();

const GameModuleList = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuthenticator((context) => [context.user]);
  const isAdmin = user?.signInUserSession?.accessToken?.payload['cognito:groups']?.includes('admin');
  const isCreator = user?.signInUserSession?.accessToken?.payload['cognito:groups']?.includes('creator');

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchGameModules();
      setModules(result.items);
    } catch (error) {
      console.error('Error fetching modules:', error);
      setError('Failed to load game modules. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this module?')) {
      try {
        await deleteGameModule(id);
        setModules(modules.filter(module => module.id !== id));
      } catch (error) {
        console.error('Error deleting module:', error);
        alert('Failed to delete the module. Please try again.');
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading game modules...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="game-module-list">
      <h2>Game Modules</h2>
      {(isAdmin || isCreator) && (
        <Link to="/create-module" className="create-module-button">
          <Plus size={16} />
          Create New Module
        </Link>
      )}
      <div className="module-grid">
        {modules.map(module => (
          <div key={module.id} className="module-card">
            <h3>{module.name}</h3>
            <p>{module.description}</p>
            <div className="module-meta">
              <span>Difficulty: {module.difficulty}</span>
              <span>Levels: {module.minLevel}-{module.maxLevel}</span>
              <span>Players: {module.recommendedPlayers}</span>
            </div>
            <div className="module-tags">
              {module.tags && module.tags.map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
            </div>
            <div className="module-actions">
              <Link to={`/module/${module.id}`} className="view-button">
                <Eye size={16} />
                View
              </Link>
              {(isAdmin || (isCreator && module.creatorId === user.username)) && (
                <>
                  <Link to={`/edit-module/${module.id}`} className="edit-button">
                    <Edit size={16} />
                    Edit
                  </Link>
                  <button onClick={() => handleDelete(module.id)} className="delete-button">
                    <Trash2 size={16} />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GameModuleList;